/**
 * Beat 9's wool shed: a small whole room, not another long industrial run.
 *
 * The two route parts construct two complete authored states.  The door leaf is
 * selected here with the shell list so a closed picture cannot accidentally get
 * an open collider (or the inverse) when the rest state grows later.
 */

import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import { strawClump } from '@/models/env/straw-clump';
import { surface } from '@/models/materials';
import { HOLLY_DUCK_HEIGHT, HOLLY_STEP_HEIGHT } from '@/models/holly';
import type { CompanionObstacle } from '@/systems/companion';
import { getZone, type ZonePalette } from '@/render/palette';
import type { Fixture, ZoneLightingOptions } from '@/render/lighting';
import { instanceGroup, type Placement } from '@/world/instancing';
import { solidMeshes, solidWorldBounds, type HallSolid } from '@/world/solids';

export const WOOL_SHED_ZONE_ID = 'wool-shed';
export const WOOL_SHED_SPAWN = { x: -3.0, y: 0.25, z: -2.6, facing: 0 } as const;

export type WoolShedPart = 1 | 2;

export interface WoolShedOptions {
  readonly part: WoolShedPart;
  /** The budget control keeps the shell and posts, but removes authored dressing. */
  readonly dressing?: boolean;
}

export const WOOL_SHED = {
  minX: -4.5,
  maxX: 4.5,
  minZ: -3.5,
  maxZ: 3.5,
  eaves: 3,
  ridge: 4.4,
  thickness: 0.12,
  doorWidth: 2.2,
  doorHeight: 2.6,
  brokenMinX: -3.45,
  brokenMaxX: -2.55,
  brokenHeight: 1.1,
} as const;
/** The north wall plane; both shed parts deliberately use this one exit threshold. */
export const WOOL_SHED_EXIT_Z = WOOL_SHED.maxZ;

const BALE = { length: 0.85, height: 0.55, width: 0.45 } as const;
const BOARD_WIDTH = 0.2;
const BOARD_GAP = 0.025;

export interface WoolShedPropPose {
  readonly position: { readonly x: number; readonly y: number; readonly z: number };
  readonly rotation: { readonly x: number; readonly y: number; readonly z: number };
}

export const WOOL_SHED_PALLET_POSES = {
  pinned: { position: { x: 2.00, y: 0.07, z: -1.55 }, rotation: { x: 0, y: 0.06, z: 0.00 } },
  tipped: { position: { x: 2.28, y: 0.58, z: -1.55 }, rotation: { x: 0, y: 0.06, z: 1.00 } },
} as const satisfies Record<'pinned' | 'tipped', WoolShedPropPose>;
/**
 * The wire, and **its first staging did not touch her.**
 *
 * A 1.60 m panel tilted 0.52 rad about its trapped end put its free end at
 * (0.47, 0.82) — 0.22 m short of Holly's rump and 0.16 m below her topline. The
 * arithmetic was right and the picture was of a sheep standing next to a piece
 * of fencing. `docs/beat-9-plan.md` asks for the opposite in as many words: *the
 * spatial arrangement must make the cause legible with sound off.*
 *
 * The second staging reached her and was still wrong: a 2.30 m panel at 0.40 rad
 * crossed her at chest and head height, so the frame showed a sheep standing
 * BEHIND a fence rather than one caught in it — and a rigid panel cannot drape
 * over a back, which is what that pose was pretending to be.
 *
 * **A sheep gets caught by the legs.** This one runs out from under the pallet
 * at x 1.80 and rises only to 0.49 m at its far end, passing UNDER her belly
 * line (0.578) the whole way, so her hind legs stand inside the mesh at about
 * mid-shin. That is a picture a rigid panel can actually make, it is what
 * actually happens to sheep, and it puts the wire, her legs and the pallet on
 * one straight line the eye follows in one move.
 *
 * The three numbers are locked together: raise the tilt and the trapped end
 * lifts off the floor, lengthen the panel and it comes up through her belly.
 */
export const WIRE = { length: 1.90, width: 0.95, rod: 0.011, longs: 8, uprights: 7 } as const;

export const WOOL_SHED_WIRE_POSES = {
  pinned: { position: { x: 0.88, y: 0.255, z: -1.50 }, rotation: { x: 0, y: -0.12, z: -0.25 } },
  // Flat, and pulled WEST as it slumps: its old trapped end has to end up clear
  // of the tipped pallet's foot at x 1.95, or the wire is still under it.
  slack: { position: { x: 0.75, y: 0.015, z: -1.58 }, rotation: { x: 0, y: -0.18, z: 0.00 } },
} as const satisfies Record<'pinned' | 'slack', WoolShedPropPose>;
/**
 * The drop bar, and **its height came down from 1.40 m because Holly cannot
 * reach 1.40 m.**
 *
 * The original number was derived from `holly.ts`'s `MUZZLE_Y`, which was wrong
 * by 95 mm and was checked only against a sibling constant in the same file.
 * With her actual mesh, her whole forward-reach envelope tops out around 1.36 m
 * — and only with her nose 0.39 m in front of her, which is what stops the pose
 * being a sheep howling at the ceiling. `src/anim/hollyClips.ts` has the solved
 * numbers; `tests/e2e/holly-rig.spec.ts` measures the built mesh against
 * `DROP_BAR.height` so the two cannot drift apart again.
 *
 * 1.25 m is still four times Rosalie's standing height. What the puzzle needs is
 * that **she** cannot reach it, and 1.25 is as far out of a 0.32 m piglet's
 * reach as 1.40 was — the bar being lower costs the beat nothing and being
 * unreachable by Holly would have cost it the beat.
 */
export const DROP_BAR = {
  height: 1.25,
  /** Just inside the north wall's boards, where the brackets are. */
  z: 3.30,
  length: 2.70,
  thickness: 0.09,
  bracketX: 1.25,
} as const;

export const WOOL_SHED_DROP_BAR_POSES = {
  down: { position: { x: 0.00, y: DROP_BAR.height, z: DROP_BAR.z }, rotation: { x: 0, y: 0.00, z: 0 } },
  // **West of centre, and that is not decoration.** `door-leaf-open` swings the
  // leaf inward through x 1.10 … 1.22; a bar dropped on the centreline spans
  // x -1.35 … 1.35 and lies straight through it. Offset to -0.55 it clears.
  lifted: { position: { x: -0.55, y: DROP_BAR.thickness / 2, z: DROP_BAR.z - 0.04 }, rotation: { x: 0, y: 0.06, z: 0 } },
} as const satisfies Record<'down' | 'lifted', WoolShedPropPose>;

/**
 * Where Holly stands to work the bar: the bar's Z, less how far her reach puts
 * her muzzle in front of her.
 *
 * NOT derived here, because `src/story/` must not import `src/world/` and the
 * puzzle is the module that owns her station. It is asserted instead —
 * `tests/e2e/wool-shed-integration.spec.ts` checks that the puzzle's
 * `LATCH_STAND` plus `HOLLY_REACH_FORWARD` lands on this bar. A gate rather than
 * a derivation, because the layering is worth more than the one literal.
 */
export const DROP_BAR_LATCH_STAND_Z = 2.91;
/** The root target is deliberately on the open south-west approach, not the bale-facing side. */
export const WOOL_SHED_PALLET_ROOT = { x: 1.55, y: 0.10, z: -1.92 } as const;

const zone: ZonePalette = (() => {
  const found = getZone(WOOL_SHED_ZONE_ID);
  if (!found) throw new Error('wool shed: wool-shed zone missing from palette');
  return found;
})();

function zoneSurface(index: number): number {
  const colour = zone.surfaces[index];
  if (colour === undefined) throw new Error(`wool shed: surface ${index} missing from wool-shed palette`);
  return colour;
}

function spanHalf(min: number, max: number): number {
  return (max - min) / 2;
}

function spanCentre(min: number, max: number): number {
  return (min + max) / 2;
}

function box(name: string, half: HallSolid['half'], pos: HallSolid['pos'], colour: number, kind: HallSolid['kind'], visible = true): HallSolid {
  return { name, half, pos, colour, kind, ...(visible ? {} : { visible: false }) };
}

/**
 * The shell colliders deliberately sit behind the timber boards.  A 25 mm board
 * gap is how daylight enters, not a 32 cm animal-sized escape route; making the
 * board meshes carry collision would turn their visual rhythm into a softlock.
 */
export function woolShedSolids(options: WoolShedOptions): HallSolid[] {
  const { part } = options;
  const { minX, maxX, minZ, maxZ, eaves, ridge, thickness, doorWidth, doorHeight, brokenMinX, brokenMaxX, brokenHeight } = WOOL_SHED;
  const halfT = thickness / 2;
  const doorMinX = -doorWidth / 2;
  const doorMaxX = doorWidth / 2;
  const roofPitch = Math.atan2(ridge - eaves, maxZ);
  const roofLength = Math.hypot(maxZ, ridge - eaves);
  const solids: HallSolid[] = [
    box('floor', { x: spanHalf(minX, maxX), y: 0.1, z: spanHalf(minZ, maxZ) }, { x: 0, y: -0.1, z: 0 }, zoneSurface(1), 'mineral'),
    // **The pitch signs, and they were the other way round.**
    //
    // `pitch` rotates about X, so for a slab centred at +z the LOW end has to
    // come out at +z. With the signs swapped the roof built a valley instead of
    // a gable: 4.4 m at both walls and 3.0 m along the centreline, which left a
    // 1.4 m band of open sky between the top of the wall boards and the roof
    // edge along the whole length of the room. It renders as a lit strip above
    // the walls and it is the first thing in the first frame of this room.
    //
    // Check it by looking UP from inside, not by reading the arithmetic — the
    // arithmetic was read twice.
    { ...box('roof-south', { x: spanHalf(minX - thickness, maxX + thickness), y: halfT, z: roofLength / 2 }, { x: 0, y: (eaves + ridge) / 2, z: -maxZ / 2 }, zoneSurface(0), 'organic'), pitch: -roofPitch },
    { ...box('roof-north', { x: spanHalf(minX - thickness, maxX + thickness), y: halfT, z: roofLength / 2 }, { x: 0, y: (eaves + ridge) / 2, z: maxZ / 2 }, zoneSurface(0), 'organic'), pitch: roofPitch },
    // Gable bodies stop at the eaves. The vent is above it, so it has no player
    // collision consequence while still remaining a real visible opening.
    box('gable-west-collider', { x: halfT, y: eaves / 2, z: spanHalf(minZ, maxZ) }, { x: minX - halfT, y: eaves / 2, z: 0 }, zoneSurface(1), 'organic', false),
    box('gable-east-collider', { x: halfT, y: eaves / 2, z: spanHalf(minZ, maxZ) }, { x: maxX + halfT, y: eaves / 2, z: 0 }, zoneSurface(1), 'organic', false),
    box('north-wall-west', { x: spanHalf(minX, doorMinX), y: eaves / 2, z: halfT }, { x: spanCentre(minX, doorMinX), y: eaves / 2, z: maxZ + halfT }, zoneSurface(1), 'organic', false),
    box('north-wall-east', { x: spanHalf(doorMaxX, maxX), y: eaves / 2, z: halfT }, { x: spanCentre(doorMaxX, maxX), y: eaves / 2, z: maxZ + halfT }, zoneSurface(1), 'organic', false),
    box('north-door-header', { x: doorWidth / 2, y: (eaves - doorHeight) / 2, z: halfT }, { x: 0, y: doorHeight + (eaves - doorHeight) / 2, z: maxZ + halfT }, zoneSurface(1), 'organic', false),
    box('south-wall-west', { x: spanHalf(minX, brokenMinX), y: eaves / 2, z: halfT }, { x: spanCentre(minX, brokenMinX), y: eaves / 2, z: minZ - halfT }, zoneSurface(1), 'organic', false),
    box('south-wall-east', { x: spanHalf(brokenMaxX, maxX), y: eaves / 2, z: halfT }, { x: spanCentre(brokenMaxX, maxX), y: eaves / 2, z: minZ - halfT }, zoneSurface(1), 'organic', false),
    box('south-broken-header', { x: (brokenMaxX - brokenMinX) / 2, y: (eaves - brokenHeight) / 2, z: halfT }, { x: spanCentre(brokenMinX, brokenMaxX), y: brokenHeight + (eaves - brokenHeight) / 2, z: minZ - halfT }, zoneSurface(1), 'organic', false),
    // Stage 1 starts just inside this cinematic entry. The authored brief also
    // requires containment through its visible break, so it remains a light gap
    // rather than a traversable route. `createBreakHurdle()` is its picture: a
    // hole with only this in it read as a door (playthrough audit row 16).
    box('south-broken-entry-blocker', { x: (brokenMaxX - brokenMinX) / 2, y: brokenHeight / 2, z: halfT }, { x: spanCentre(brokenMinX, brokenMaxX), y: brokenHeight / 2, z: minZ - halfT }, zoneSurface(1), 'organic', false),
    box('king-post-west', { x: 0.09, y: ridge / 2, z: 0.09 }, { x: -1.5, y: ridge / 2, z: 0 }, zoneSurface(0), 'organic'),
    box('king-post-east', { x: 0.09, y: ridge / 2, z: 0.09 }, { x: 1.5, y: ridge / 2, z: 0 }, zoneSurface(0), 'organic'),
    box('ridge-beam', { x: spanHalf(minX, maxX), y: 0.09, z: 0.09 }, { x: 0, y: ridge - 0.09, z: 0 }, zoneSurface(0), 'organic'),
  ];
  // The control frame must be physically bare as well as visually bare: an
  // invisible bale collider would make its budget probe a different room to walk.
  if (options.dressing !== false) {
    solids.push(box('wool-bale-stack', { x: (4.35 - 2.6) / 2, y: BALE.height, z: (2 - -3) / 2 }, { x: (4.35 + 2.6) / 2, y: BALE.height, z: (-3 + 2) / 2 }, zoneSurface(3), 'organic', false));
  }

  if (part === 1) {
    // These are separate authored states, rather than a dynamically rotated box:
    // the integrator swaps exactly one named collider with the visible pose, so
    // a released wire cannot remain physically pinned after the pallet tips.
    solids.push(
      box('pallet-pinned', { x: 0.6, y: 0.07, z: 0.4 }, WOOL_SHED_PALLET_POSES.pinned.position, zoneSurface(2), 'organic', false),
      box('pallet-tipped', { x: 0.3835, y: 0.5425, z: 0.4 }, WOOL_SHED_PALLET_POSES.tipped.position, zoneSurface(2), 'organic', false),
      box('drop-bar', { x: DROP_BAR.length / 2, y: DROP_BAR.thickness / 2, z: DROP_BAR.thickness / 2 }, WOOL_SHED_DROP_BAR_POSES.down.position, zoneSurface(2), 'organic', false),
    );
    solids.push(box('door-leaf', { x: doorWidth / 2, y: doorHeight / 2, z: halfT }, { x: 0, y: doorHeight / 2, z: maxZ }, zoneSurface(2), 'organic', false));
  } else {
    // Its collider is intentionally absent in rest state. The visible leaf is
    // made from the same part selection below and swings clear against this wall.
    solids.push(box('door-leaf-open', { x: halfT, y: doorHeight / 2, z: doorWidth / 2 }, { x: doorMaxX + halfT, y: doorHeight / 2, z: maxZ - doorWidth / 2 }, zoneSurface(2), 'organic', false));
  }
  return solids;
}

/**
 * Derive Holly's collision from the very solids list that draws the shell and
 * builds Rapier colliders, so a moved solid cannot leave her collision behind.
 * Invisible collider-only boxes remain solid; `visible` changes rendering, not
 * the physical room.
 */
/**
 * The edge of the built world, for a companion who has nowhere to go past it.
 *
 * **This is the one obstacle in Holly's list that is not a solid, and it is the
 * only exception to D-158's mirror rule.** Every other entry is derived from
 * `woolShedSolids()` so that a moved wall moves her collision with it; this one
 * has no collider, no mesh and nothing to mirror, because what it represents is
 * that part 1 has no exterior.
 *
 * It exists because of what she does at the end of the beat. The swing returns
 * her to `following` (D-160), and following means keeping a standoff — so
 * Rosalie walking up behind her to leave *pushes her out through the open door*.
 * Measured on the first build: (0.077, 3.787), which is 0.29 m past a floor that
 * ends at 3.5, standing on the background, for as long as the player chooses to
 * wait. An independent review found it; the note filed against it said the
 * closing fade covered it, and that was wrong — the fade does not start until
 * Rosalie crosses, and she is pushed out before that.
 *
 * Rosalie passes through it because it is not there for her: it is not a
 * collider, it is a bound on a companion's authored space. `LATCH_STAND` sits
 * 0.23 m clear of it once inflated by her radius, and `Companion`'s constructor
 * throws on a station its obstacles have swallowed, so the beat cannot boot with
 * this box in the wrong place.
 */
export const WOOL_SHED_NORTH_BOUND: CompanionObstacle = {
  id: 'north-threshold-bound',
  min: { x: -WOOL_SHED.doorWidth / 2 - 0.05, z: WOOL_SHED.maxZ - 0.06 },
  max: { x: WOOL_SHED.doorWidth / 2 + 0.05, z: WOOL_SHED.maxZ + 0.06 },
};

/**
 * One solid's companion-navigation footprint, or `null` if Holly can step
 * under or over it entirely.
 *
 * **This used to project `half.x`/`half.z` straight through, unrotated, and
 * ignore yaw altogether** (F2, `docs/engine-architecture-audit.md`) — correct
 * for every solid actually shipped, because none of them yaw, but silently
 * wrong for the first one that would. `solidWorldBounds` is the same
 * transform the mesh and the collider now agree on (F1), so this footprint
 * cannot drift from either of them the way the old hand-rolled pitch
 * projection could have.
 *
 * The X/Z box this returns is a conservative AABB around a possibly-rotated
 * solid, not an oriented obstacle — Holly's navigation stays axis-aligned on
 * purpose (`docs/engine-architecture-audit.md` F2: "this task does not
 * introduce an oriented-obstacle solver").
 */
export function companionObstacleFromSolid(solid: HallSolid): CompanionObstacle | null {
  const bounds = solidWorldBounds(solid);
  if (bounds.max.y <= HOLLY_STEP_HEIGHT || bounds.min.y >= HOLLY_DUCK_HEIGHT) return null;
  return {
    id: solid.name,
    min: { x: bounds.min.x, z: bounds.min.z },
    max: { x: bounds.max.x, z: bounds.max.z },
  };
}

export function woolShedCompanionObstacles(options: WoolShedOptions): CompanionObstacle[] {
  // **Part 1 is handed part 2's swung-open leaf as well, and that is not a
  // layering mistake.** During part 1 the door OPENS: `main.ts` creates exactly
  // that collider out of `woolShedSolids({ part: 2 })` when the swing finishes,
  // because the shut leaf and the open one are two authored poses of one thing.
  // Holly's list has to contain both for the same reason, or she is the only
  // body in the room that can walk through a door standing wide open — which is
  // precisely what an independent review found here before this line existed.
  // Which of the two is SOLID at any moment is the puzzle's call, not this
  // function's; see `WoolShedPuzzle`'s `#obstacleIds`.
  const solids = options.part === 1
    ? [...woolShedSolids(options), ...woolShedSolids({ ...options, part: 2 }).filter((solid) => solid.name === 'door-leaf-open')]
    : woolShedSolids(options);
  const derived = solids.flatMap((solid) => {
    const obstacle = companionObstacleFromSolid(solid);
    return obstacle === null ? [] : [obstacle];
  });
  // Part 1 only. Part 2 constructs no companion at all — Holly is a frozen
  // snapshot there — so a bound would be a box nothing consults.
  return options.part === 1 ? [...derived, WOOL_SHED_NORTH_BOUND] : derived;
}

function boardSource(height: number): THREE.Group {
  const source = new THREE.Group();
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(BOARD_WIDTH, height, WOOL_SHED.thickness), surface('organic', zoneSurface(2)));
  mesh.position.y = height / 2;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  source.add(mesh);
  return source;
}

function boardXs(min: number, max: number): number[] {
  const out: number[] = [];
  for (let x = min + BOARD_WIDTH / 2; x < max - 0.001; x += BOARD_WIDTH + BOARD_GAP) out.push(x);
  return out;
}

function boardsForSpan(min: number, max: number, z: number, y = 0): Placement[] {
  return boardXs(min, max).map((x) => ({ x, y, z }));
}

function addLongWallBoards(group: THREE.Group): void {
  const { minX, maxX, minZ, maxZ, eaves, doorWidth, doorHeight, brokenMinX, brokenMaxX, brokenHeight } = WOOL_SHED;
  const doorMinX = -doorWidth / 2;
  const doorMaxX = doorWidth / 2;
  group.add(instanceGroup(boardSource(eaves), [
    ...boardsForSpan(minX, doorMinX, maxZ),
    ...boardsForSpan(doorMaxX, maxX, maxZ),
    ...boardsForSpan(minX, brokenMinX, minZ),
    ...boardsForSpan(brokenMaxX, maxX, minZ),
  ], 'wool-shed-wall-boards-full'));
  group.add(instanceGroup(boardSource(eaves - doorHeight), boardsForSpan(doorMinX, doorMaxX, maxZ, doorHeight), 'wool-shed-door-header-boards'));
  group.add(instanceGroup(boardSource(eaves - brokenHeight), boardsForSpan(brokenMinX, brokenMaxX, minZ, brokenHeight), 'wool-shed-broken-header-boards'));
}

/**
 * The gable boards, **merged rather than instanced**, and that is the whole
 * point of this function's shape.
 *
 * Every board in a gable is a different height — that is what makes it a gable —
 * so instancing has nothing to repeat. The first pass instanced anyway, bucketed
 * by height rounded to 50 mm, and bought one `InstancedMesh` per bucket: about
 * thirty draw calls a gable, sixty for the room, in a 9 × 7 m shed whose census
 * came back at 253 against a cull corridor that dresses 26 m of corridor for 159.
 *
 * `instanceGroup` is for repetition. Thirty-one boxes that are each unique are a
 * merge, and the merge is 1 draw call and 372 triangles.
 */
function addGableBoards(group: THREE.Group, x: number, east: boolean): void {
  const pieces: THREE.BufferGeometry[] = [];
  const add = (height: number, y: number, z: number): void => {
    if (height <= 0.02) return;
    const board = new THREE.BoxGeometry(WOOL_SHED.thickness, height, BOARD_WIDTH);
    board.translate(x, y + height / 2, z);
    pieces.push(board);
  };
  for (let z = WOOL_SHED.minZ + BOARD_WIDTH / 2; z < WOOL_SHED.maxZ - 0.001; z += BOARD_WIDTH + BOARD_GAP) {
    const height = WOOL_SHED.eaves + (WOOL_SHED.ridge - WOOL_SHED.eaves) * (1 - Math.abs(z) / WOOL_SHED.maxZ);
    if (east && Math.abs(z) < 0.5) {
      // This is actual missing timber, not a blue panel: the upper sliver and
      // lower board leave the authored 1.0 × 0.5 m sky vent between them.
      add(3.65, 0, z);
      add(height - 4.15, 4.15, z);
    } else {
      add(height, 0, z);
    }
  }
  const merged = mergeGeometries(pieces, false);
  if (!merged) throw new Error('wool shed: gable board merge failed');
  const mesh = new THREE.Mesh(merged, surface('organic', zoneSurface(2)));
  mesh.name = `wool-shed-gable-${east ? 'east' : 'west'}`;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  group.add(mesh);
}

function applyPose(group: THREE.Group, pose: WoolShedPropPose): void {
  group.position.set(pose.position.x, pose.position.y, pose.position.z);
  group.rotation.set(pose.rotation.x, pose.rotation.y, pose.rotation.z);
}

/**
 * The door leaf's two poses, and **it is a prop now because it never opened.**
 *
 * The leaf used to be built directly into whichever pose its part needed, which
 * was fine while the two parts were separate authored states and nothing moved
 * between them. Beat 9's second gate moves between them: the bar comes off and
 * the door opens. The first build swapped the leaf's COLLIDER and left its mesh
 * where it was, so solving the whole puzzle let Rosalie walk through a door that
 * was still visibly shut — found by an independent review, and invisible to the
 * integration gate, which asserted `doorOpen`, the bar's height and that she
 * could traverse, and never once where the leaf was.
 *
 * Part 2 still constructs it directly in the open pose. Same numbers, one place.
 */
export const WOOL_SHED_DOOR_POSES = {
  shut: {
    position: { x: 0, y: WOOL_SHED.doorHeight / 2, z: WOOL_SHED.maxZ },
    rotation: { x: 0, y: 0, z: 0 },
  },
  // The hinge side stays just inside the north wall. This clears the 2.2 m
  // doorway in both image and collision, unlike merely hiding its collider.
  open: {
    position: {
      x: WOOL_SHED.doorWidth / 2,
      y: WOOL_SHED.doorHeight / 2,
      z: WOOL_SHED.maxZ - WOOL_SHED.doorWidth / 2,
    },
    rotation: { x: 0, y: Math.PI / 2, z: 0 },
  },
} as const satisfies Record<'shut' | 'open', WoolShedPropPose>;

function createDoorLeaf(part: WoolShedPart): THREE.Group {
  const leaf = new THREE.Group();
  // One name in both parts. The integrator poses it by name and part 1's leaf
  // is no longer permanently shut, so a name that says "closed" would be a lie
  // in the exact frame it matters.
  leaf.name = 'wool-shed-door-leaf';
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(WOOL_SHED.doorWidth, WOOL_SHED.doorHeight, WOOL_SHED.thickness), surface('organic', zoneSurface(2)));
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  leaf.add(mesh);
  applyPose(leaf, part === 1 ? WOOL_SHED_DOOR_POSES.shut : WOOL_SHED_DOOR_POSES.open);
  return leaf;
}

/** Unique pallet boards merge because using thirteen one-off meshes repeats the gable draw-call failure. */
function createPallet(): THREE.Group {
  const pieces: THREE.BufferGeometry[] = [];
  for (let index = 0; index < 7; index += 1) {
    const board = new THREE.BoxGeometry(0.10, 0.022, 0.80);
    board.translate(-0.55 + index * (1.10 / 6), 0.059, 0);
    pieces.push(board);
  }
  for (const z of [-0.27, 0, 0.27]) {
    const bearer = new THREE.BoxGeometry(1.20, 0.038, 0.10);
    bearer.translate(0, 0.029, z);
    pieces.push(bearer);
  }
  for (const x of [-0.40, 0, 0.40]) {
    const board = new THREE.BoxGeometry(0.10, 0.022, 0.80);
    board.translate(x, -0.059, 0);
    pieces.push(board);
  }
  const merged = mergeGeometries(pieces, false);
  if (!merged) throw new Error('wool shed: pallet merge failed');
  const group = new THREE.Group();
  group.name = 'wool-shed-pallet';
  const mesh = new THREE.Mesh(merged, surface('organic', zoneSurface(2)));
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  group.add(mesh);
  applyPose(group, WOOL_SHED_PALLET_POSES.pinned);
  return group;
}

/** The visual wire deliberately has no solid: puzzle state, rather than a 8 mm rod, holds Holly. */
function createWire(): THREE.Group {
  const pieces: THREE.BufferGeometry[] = [];
  for (let index = 0; index < WIRE.longs; index += 1) {
    const wire = new THREE.CylinderGeometry(WIRE.rod, WIRE.rod, WIRE.length, 5, 1, true);
    wire.rotateZ(Math.PI / 2);
    wire.translate(0, 0, -WIRE.width / 2 + index * (WIRE.width / (WIRE.longs - 1)));
    pieces.push(wire);
  }
  for (let index = 0; index < WIRE.uprights; index += 1) {
    const upright = new THREE.CylinderGeometry(WIRE.rod, WIRE.rod, WIRE.width, 5, 1, true);
    upright.rotateX(Math.PI / 2);
    upright.translate(-WIRE.length / 2 + index * (WIRE.length / (WIRE.uprights - 1)), 0, 0);
    pieces.push(upright);
  }
  const merged = mergeGeometries(pieces, false);
  if (!merged) throw new Error('wool shed: wire merge failed');
  const group = new THREE.Group();
  group.name = 'wool-shed-wire';
  const mesh = new THREE.Mesh(merged, surface('metal', zoneSurface(3)));
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  group.add(mesh);
  applyPose(group, WOOL_SHED_WIRE_POSES.pinned);
  return group;
}

function createDropBar(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'wool-shed-drop-bar';
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(DROP_BAR.length, DROP_BAR.thickness, DROP_BAR.thickness), surface('organic', zoneSurface(2)));
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  group.add(mesh);
  applyPose(group, WOOL_SHED_DROP_BAR_POSES.down);
  return group;
}

/** Puzzle props are constructed once; state changes only their named root transforms. */
export function createWoolShedProps(): THREE.Group {
  const props = new THREE.Group();
  props.name = 'wool-shed-puzzle-props';
  props.add(createPallet(), createWire(), createDropBar());
  // Two boxes that never move: a merge and one draw call, not two clones of a
  // template mesh that was then never added to anything. They also both carried
  // the same name, so `getObjectByName` could only ever find one of them.
  const bracketPieces: THREE.BufferGeometry[] = [];
  for (const x of [-DROP_BAR.bracketX, DROP_BAR.bracketX]) {
    const bracket = new THREE.BoxGeometry(0.10, 0.24, 0.10);
    bracket.translate(x, DROP_BAR.height, DROP_BAR.z + 0.06);
    bracketPieces.push(bracket);
  }
  const bracketGeometry = mergeGeometries(bracketPieces, false);
  if (!bracketGeometry) throw new Error('wool shed: drop bar bracket merge failed');
  const brackets = new THREE.Mesh(bracketGeometry, surface('organic', zoneSurface(2)));
  brackets.name = 'wool-shed-drop-bar-brackets';
  brackets.castShadow = true;
  brackets.receiveShadow = true;
  props.add(brackets);
  return props;
}

/**
 * A sheep hurdle leant against the outside of the south break, and **it is
 * the picture of `south-broken-entry-blocker`, which had none.**
 *
 * The break is 0.9 × 1.1 m of missing boards a metre behind her spawn, and
 * before this it drew as a clean doorway onto flat sky with an invisible wall
 * in it (playthrough audit row 16, `room-shots` view `south-break`). A hole
 * the size of a door reads as a door. The hurdle says "closed" with the
 * vocabulary of a sheep shed, and its open rails keep the break doing the one
 * thing it was built for, which is letting daylight in. It overlaps the boards
 * on both sides so no edge of the hole shows beside it, and it sits outside
 * the wall plane so it never narrows the room she walks in.
 */
function createBreakHurdle(): THREE.Group {
  const { brokenMinX, brokenMaxX, minZ, thickness } = WOOL_SHED;
  const width = brokenMaxX - brokenMinX + 0.3;
  const height = 1.2;
  const pieces: THREE.BufferGeometry[] = [];
  for (const x of [-width / 2 + 0.04, width / 2 - 0.04]) {
    const stile = new THREE.BoxGeometry(0.07, height, 0.05);
    stile.translate(x, height / 2, 0);
    pieces.push(stile);
  }
  for (const y of [0.16, 0.42, 0.68, 0.94, 1.14]) {
    const rail = new THREE.BoxGeometry(width, 0.075, 0.035);
    rail.translate(0, y, 0);
    pieces.push(rail);
  }
  const brace = new THREE.BoxGeometry(0.06, Math.hypot(width - 0.1, 0.98), 0.03);
  brace.rotateZ(Math.atan2(width - 0.1, 0.98));
  brace.translate(0, 0.65, -0.03);
  pieces.push(brace);
  const merged = mergeGeometries(pieces, false);
  if (!merged) throw new Error('wool shed: hurdle merge failed');
  const group = new THREE.Group();
  group.name = 'wool-shed-break-hurdle';
  const mesh = new THREE.Mesh(merged, surface('organic', zoneSurface(2)));
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  group.add(mesh);
  // Feet a hand's width out from the boards, top resting on them: a lean,
  // not a fitted panel, because nobody fitted it.
  group.position.set((brokenMinX + brokenMaxX) / 2, 0, minZ - thickness - 0.12);
  group.rotation.x = 0.09;
  return group;
}

function balePlacements(): Placement[] {
  const placements: Placement[] = [];
  for (const y of [0, BALE.height]) {
    for (let x = 2.6 + BALE.length / 2; x + BALE.length / 2 <= 4.35 + 0.001; x += BALE.length) {
      for (let z = -3 + BALE.width / 2; z + BALE.width / 2 <= 2 + 0.001; z += BALE.width) placements.push({ x, y, z });
    }
  }
  return placements;
}

function dressWoolShed(part: WoolShedPart): THREE.Group {
  const dressing = new THREE.Group();
  dressing.name = 'wool-shed-dressing';
  addLongWallBoards(dressing);
  addGableBoards(dressing, WOOL_SHED.minX, false);
  addGableBoards(dressing, WOOL_SHED.maxX, true);
  dressing.add(createDoorLeaf(part), createBreakHurdle());

  const baleSource = new THREE.Group();
  // **Two segments, not four.** A rounded box at segment count 4 is roughly
  // 4 000 triangles, and there are 44 bales in the stack: the first census came
  // back at 196 000 triangles for a 9 × 7 m shed, of which 176 000 were these.
  // At 2 the corner is still soft — which is the whole reason a bale is not a
  // box — and the stack costs a quarter as much. It is one instanced draw call
  // either way, so nothing about this is visible in the draw-call column, which
  // is exactly how it got to 196 000 without anybody noticing.
  const baleMesh = new THREE.Mesh(new RoundedBoxGeometry(BALE.length, BALE.height, BALE.width, 2, 0.07), surface('fabric', zoneSurface(3)));
  baleMesh.position.y = BALE.height / 2;
  baleMesh.castShadow = true;
  baleMesh.receiveShadow = true;
  baleSource.add(baleMesh);
  dressing.add(instanceGroup(baleSource, balePlacements(), 'wool-bales'));

  const straw = zone.straw;
  if (straw === undefined) throw new Error('wool shed: straw colour missing from wool-shed palette');
  dressing.add(instanceGroup(strawClump(9, straw), [
    { x: -4.0, z: -0.35 }, { x: -3.55, z: 0.15 }, { x: -3.1, z: 0.65 },
    { x: -3.8, z: 1.15 }, { x: -3.35, z: 1.65 }, { x: -2.8, z: 2.0 },
  ], 'wool-shed-bedding'));
  return dressing;
}

/** One quiet exterior bed; Beat 9 does not invent audio before its audio brief. */
export function woolShedAmbience(): { id: string; at: { x: number; y: number; z: number }; gain: number }[] {
  return [];
}

/**
 * What the open doorway shows in the rest state, and **it used to be a lightbox.**
 *
 * `createZoneLighting` hands back `zone.fog` as the background, which is right
 * for part 1 — a 2.2 m opening onto a bright afternoon is supposed to blow out.
 * Part 2 is the same opening at night, and the same colour there made the one
 * thing the room is arranged around read as a flat white panel hung in a dark
 * shed: brighter than every lit surface in it, brighter than the lamp, and with
 * no depth of any kind. It was the first thing in the frame and it was the worst
 * thing in it.
 *
 * The colour is the zone's own `fill`, not a new one. That field is already
 * documented as this room's sky — *"the open door and the gable gap admit sky,
 * so everything the sun slats miss is filled by it"* — so the night version of
 * the doorway is that same sky, graded down. 0.16 puts it below the lamp-lit
 * jamb beside it, which is what makes the opening read as a hole rather than as
 * a source, and it leaves the doorway cool against a warm room. That contrast is
 * the scene's argument: the shed is warm because the night is not.
 *
 * `docs/act-3-plan.md` has beat 10 set in a *moonlit* exterior, saturated and
 * cold. This is the same night seen from indoors, one beat early.
 */
export function woolShedNightSky(): THREE.Color {
  return new THREE.Color(zone.fill).multiplyScalar(0.16);
}

/**
 * The one practical, and **it hangs over the bedding rather than over the door.**
 *
 * It used to be at (0.8, 2.8), beside the north doorway, which is where a real
 * shed would put a light and which made the rest scene say the wrong thing. The
 * two animals bed down at the west end; a lamp at the door left them in the dark
 * at the far corner, lit the exit, and produced a frame whose brightest object
 * was the way out. That is beat 10's argument, not this one.
 *
 * Over the straw, the room reads as what S-8 needs it to be: a warm pool with
 * both animals in it, and the exit a cold blue rectangle five metres away that
 * nothing is pushing them toward. Same fitting, same intensity band — it is far
 * below any corridor fixture on purpose — moved four metres.
 *
 * `range` covers the bedding and reaches the authored spawn at 3.5 m, so Rosalie
 * wakes at the edge of the light rather than outside it.
 */
export function woolShedFixtures(part: WoolShedPart): Fixture[] {
  return part === 2
    ? [{ kind: 'point', position: { x: -2.9, y: 2.2, z: 0.9 }, intensity: 3, range: 4.5 }]
    : [];
}

/**
 * The two rigs, one per part. Moved here from `main.ts` with their reasoning
 * (session 66): the room declares its light, the host only applies it.
 */
export function woolShedLighting(part: WoolShedPart): ZoneLightingOptions {
  return part === 1 ? {
    fixtures: woolShedFixtures(part),
    keyDirection: { x: 0.55, y: 0.62, z: -0.56 },
    keyIntensity: 0.85,
    shadowExtent: 12,
    // Generous, and it is the same call the service corridor makes for the
    // opposite reason. That room is bright because it is painted blockwork
    // under five tubes; this one is bright because it is a **leaky box in
    // daylight** — every board gap, the gable vent and a 2.2 m doorway all
    // admit sky, and light gets in here whether or not the sun is on that
    // wall. The first pass ran 0.30/0.14 and the room came back mud: one
    // brown value from floor to ridge, in the beat whose whole job is to be
    // the first warm room in the game.
    // 0.30 -> 0.46 -> 0.58, and the last step was the first one MEASURED
    // rather than argued. `ROOM=wool-shed SWEEP=hemi npm run shots:room`
    // renders the same view at six levels; at 0.10 the bales are grey-green
    // and the room is cold, at 0.70 it is warm and flat, and 0.58 is where the
    // wool starts reading as wool while the shadow structure survives.
    //
    // The hemisphere's sky colour is the zone KEY and its ground colour is the
    // zone FILL (see `createZoneLighting`), so raising it is not a brightness
    // control here — it is a WARMTH control, because it raises a warm sky
    // against a cool ground. The backlog said the opposite, on reasoning, and
    // the sweep disproved it in one run.
    hemiIntensity: 0.58,
    ambientIntensity: 0.26,
    // Below 1, which no other room does. Nothing in here is a fitting, so
    // there is no practical to anchor to — the reference has to be the
    // daylight itself, and anchoring at 1.0 graded the whole room down to
    // meet a light source that does not exist.
    exposureReference: 0.60,
  } : {
    fixtures: woolShedFixtures(part),
    keyDirection: { x: -0.30, y: 0.85, z: 0.42 },
    keyIntensity: 0.06,
    shadowExtent: 12,
    hemiIntensity: 0.10,
    ambientIntensity: 0.07,
    // The lamp IS the reference here, and that is the whole difference between
    // the two parts. Part 1 has no fitting and anchors on daylight; this one
    // has one small lamp and everything outside its pool should fall away.
    exposureReference: 1.2,
  };
}

export function createWoolShed(options: WoolShedOptions): THREE.Group {
  const room = new THREE.Group();
  room.name = 'wool-shed';
  room.add(solidMeshes(woolShedSolids(options), 'wool-shed-shell'));
  if (options.dressing !== false) room.add(dressWoolShed(options.part));
  return room;
}
